import { useMemo } from 'react';
import { Vector3 } from 'three';

import { DimensionEdge } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionEdge';
import {
    createAngleVisualization,
    createCornerGeometry,
    createEdgeDimensionTexts,
    getCornerPoi
} from 'app/modules/wallforming/components/scenes/objects/dimensions/utils/dimensionUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { DimensionEdgeResult } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionEdgeResult';

interface CreateEdgeDimensioningProps {
    mid: Vector3;
    dir: Vector3;
    selectedPoiIndex: number | undefined;
}

const defaultResult: DimensionEdgeResult = {
    edges: [],
    texts: [],
    center: null,
    edgeLength: 0,
    angle: 0,
    faceLines: [],
    angleArc: [],
    angleLabelPosition: null,
    euler: null
};

export const useCreateEdgeDimensioning = ({
    selectedPoiIndex,
    mid,
    dir
}: CreateEdgeDimensioningProps): DimensionEdgeResult => {
    const unit = usePlacementStore((state) => state.dimensionSettings.unit);
    const heightScale = usePlacementStore((state) =>
        state.dimensionSettings.displayMode === 'projection' ? state.dimensionSettings.projectionVerticalScale : 0.4
    );

    return useMemo(() => {
        const state = usePlacementStore.getState();
        const poi = getCornerPoi(selectedPoiIndex, state);
        if (!poi) {
            return defaultResult;
        }

        const geometry = createCornerGeometry(poi, mid, dir, heightScale);
        if (!geometry) {
            return defaultResult;
        }

        const angleVisualization = createAngleVisualization(
            poi,
            geometry.edgeEnd,
            geometry.edgeDirection,
            geometry.wallDirectionA,
            geometry.wallDirectionB,
            geometry.edgeLength
        );

        const texts = createEdgeDimensionTexts(
            poi.poiGuid,
            geometry.edgeLength,
            geometry.labelPosition,
            angleVisualization.angle,
            angleVisualization.angleLabelPosition,
            unit
        );

        const dimensionEdge: DimensionEdge = {
            id: poi.poiGuid,
            start: geometry.edgeStart.clone(),
            end: geometry.edgeEnd.clone(),
            labelPosition: geometry.labelPosition.clone()
        };

        return {
            edges: [dimensionEdge],
            texts,
            center: geometry.center.clone().sub(mid),
            edgeLength: geometry.edgeLength,
            angle: angleVisualization.angle,
            faceLines: angleVisualization.faceLines,
            angleArc: angleVisualization.angleArc,
            angleLabelPosition: angleVisualization.angleLabelPosition,
            euler: geometry.textRotation
        };
    }, [selectedPoiIndex, dir, mid, unit, heightScale]);
};
